import React from "react";

import { captureException } from "@/lib/sentry";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so a crashing page shows a readable message
 * instead of a blank white screen. In dev the message/stack are displayed to
 * aid debugging; the error is always logged to the console.
 */
class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Render errors caught here are "handled", so they never reach Sentry's
    // global handler — report them explicitly. No-op if Sentry is disabled.
    captureException(error);
    // eslint-disable-next-line no-console
    console.error("Unhandled render error:", error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ error: null });
    window.location.reload();
  };

  override render(): React.ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">
          Something went wrong
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">
          The page hit an unexpected error. Try reloading — if it keeps
          happening, please contact support.
        </p>
        {import.meta.env.DEV && (
          <pre className="max-w-2xl overflow-auto rounded-lg border border-border bg-muted p-3 text-left text-xs text-red-600 dark:text-red-400">
            {error.message}
            {"\n\n"}
            {error.stack}
          </pre>
        )}
        <button
          type="button"
          onClick={this.handleReload}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;